import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { Dashboard } from './pages/Dashboard';
import { AlertsPage } from './pages/Alerts';
import { ConfigPage } from './pages/Config';
import { LoginPage } from './pages/Login';

function RequireAuth({
  children,
  adminOnly = false,
}: {
  children: ReactNode;
  adminOnly?: boolean;
}) {
  const { loading, session } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cockpit-bg text-cockpit-dim">
        <p className="text-xs uppercase tracking-widest">checking session…</p>
      </div>
    );
  }

  if (!session.authenticated) {
    const to = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?to=${to}`} replace />;
  }

  if (adminOnly && session.user?.role !== 'admin') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cockpit-bg text-cockpit-text">
        <main className="max-w-md rounded border border-cockpit-border bg-cockpit-panel p-6 text-center">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-cockpit-warn">
            Admin only
          </h2>
          <p className="mt-2 text-xs text-cockpit-dim">
            You&apos;re signed in as {session.user?.email}, but this page requires the admin role.
            Ask an existing admin to promote you.
          </p>
        </main>
      </div>
    );
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/"
            element={
              <RequireAuth>
                <Dashboard />
              </RequireAuth>
            }
          />
          <Route
            path="/alerts"
            element={
              <RequireAuth>
                <AlertsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/config"
            element={
              <RequireAuth>
                <ConfigPage />
              </RequireAuth>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
